import React, { useMemo, useState } from 'react';
import {
  Tab,
  TabList,
  ToolbarButton,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  Button,
  Checkbox,
} from '@fluentui/react-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings24Regular } from '@fluentui/react-icons';
import { useUserPrefs } from '@/context/UserPrefsContext';
import { categoryLabels } from '@/i18n/categoryLabels';
import type { CategoryKey, TabItem } from '@/types';
import { uiText } from '@/i18n/locales';

export const routineCategoryKeys: CategoryKey[] = [
  'buy_sell',
  'dating',
  'events',
  'jobs',
  'housing',
  'cars',
];

const DEFAULT_EXCLUDE: CategoryKey[] = ['city', '25'];

export const CategoryTabs: React.FC = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { prefs, setPrefs } = useUserPrefs();

  const t = uiText[prefs.language] ?? uiText.en;
  const lang = prefs.language ?? 'en';
  const labels = categoryLabels[lang] ?? categoryLabels.en;

  const [showSettings, setShowSettings] = useState(false);

  const [visibleTabs, setVisibleTabs] = useState<CategoryKey[]>(() => {
    const saved = prefs.tabPrefs as CategoryKey[] | undefined;
    if (saved?.length) return saved;
    return baseTabs
      .map(t => t.key as CategoryKey)
      .filter(k => !DEFAULT_EXCLUDE.includes(k));
  });

  const cityBasePath =
    prefs.country && prefs.state && prefs.city
      ? `/${prefs.country}/${prefs.state}/${prefs.city}`
      : null;

  /* ---------- Tabs ---------- */
const tabs: TabItem[] = useMemo(() => {
  const base = baseTabs
    .filter(t => DEFAULT_EXCLUDE.includes(t.key as CategoryKey) || visibleTabs.includes(t.key as CategoryKey))
    .map(t => ({
      ...t,
      label: labels[t.key as CategoryKey] ?? t.path, // force translation here
    }));

  if (!cityBasePath) return base;

  const cityTab: TabItem = {
    key: 'city',
    label: `${prefs.city} ${labels.city}`, // city label
    path: cityBasePath,
  };

  const routineTabs: TabItem[] = routineCategoryKeys.map(key => ({
    key,
    routineKey: key,
    label: `${prefs.city} ${labels[key] ?? key}`,
    path: `${cityBasePath}/${key}`,
  }));

  return [...base.filter(t => t.key !== 'city'), cityTab, ...routineTabs];
}, [cityBasePath, visibleTabs, labels, prefs.city]);


  /* ---------- Active Tab ---------- */
const activeKey = useMemo(() => {
  //console.log('loc:', loc); 
   var path = loc.pathname.replace(/\/$/, ''); // remove trailing slash
  //console.log('Location Pathname:', path);
  // path = path.replace(/^\/+/, ''); // remove leading slash for comparison
  // //console.log('Location Pathname2:', path);
  const exact = tabs.find(t => path === t.path);
  //console.log('Location Pathname3:', path,'exact :', exact);
  if (exact) return exact.key;

  const nested = tabs.find(t => path.endsWith(t.path ));
  //console.log('Location Pathname4:', path,'nested :', nested);
  if (nested) return nested.key;
//console.log('No exact or nested match foundtabs[0]?.key,',tabs[0]?.key);
  // fallback to first visible tab
  return tabs[0]?.key ?? '25';
}, [loc.pathname, tabs]);
//console.log('Active Key:', activeKey);
// //console.log('Tabs array:', tabs.map(t => ({key: t.key, path: t.path})));

  /* ---------- Render ---------- */
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <TabList
        selectedValue={activeKey}
        onTabSelect={(_, d) => {
            //console.log('Tab select value:', d.value);
  //console.log('Available tabs:', tabs.map(t => t.key));
          const key = String(d.value);
          const tab = tabs.find(t => String(t.key) === key);
          //console.log('Selected tab for key:', loc.pathname , tab?.path);
          if (!tab || loc.pathname.replace(/^\/+/, '') === tab.path) return;

          //console.log('Key:', key, 'Selected tab for key:', loc.pathname ,'tab.path:', tab?.path); 
          const isBaseTab = baseTabs.some(b => b.key === tab.key);
          if (isBaseTab && loc.pathname !== tab.path) {
            nav(tab.path.startsWith('/') ? tab.path : '/' + tab.path); // update URL
          }else if(!isBaseTab ){
            nav(tab?.path ?? '');
          }
          // if (tab) nav(tab.path, { replace: true });; // now all paths are absolute
        }}


        style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 2px', flex: 1 }}
      >
        {tabs.map(t => (
          <Tab
            key={t.key}
            value={t.key}
            style={{ padding: '4px 8px', minWidth: 'auto', fontSize: 13 }}
          >
            {t.label ?? t.path}
          </Tab>
        ))}

        <ToolbarButton
          icon={<Settings24Regular />}
          appearance="subtle"
          aria-label="Customize tabs"
          onClick={() => setShowSettings(true)}
        />
      </TabList>

      {/* Settings dialog unchanged */}
      <Dialog open={showSettings} onOpenChange={(_, d) => setShowSettings(d.open)}>
        <DialogSurface style={{ width: '95%', maxWidth: 520 }}>
          <DialogBody>
            <DialogTitle>{t.chooseNewsCategory}</DialogTitle>

            {baseTabs
              .filter(t => !DEFAULT_EXCLUDE.includes(t.key as CategoryKey))
              .map(t => (
                <Checkbox
                  key={t.key}
                  label={labels[t.key as CategoryKey] ?? t.path}
                  checked={visibleTabs.includes(t.key as CategoryKey)}
                  onChange={() =>
                    setVisibleTabs(v =>
                      v.includes(t.key as CategoryKey)
                        ? v.filter(k => k !== t.key)
                        : [...v, t.key as CategoryKey]
                    )
                  }
                />
              ))}

            <DialogActions>
              <Button appearance="primary" onClick={() => {
                setPrefs({ ...prefs, tabPrefs: visibleTabs });
                setShowSettings(false);
              }}>
                {t.save ?? 'Save'}
              </Button>
              <Button onClick={() => setShowSettings(false)}>
                {t.cancel ?? 'Cancel'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

/* ---------- Base Tabs ---------- */
export const baseTabs: TabItem[] = [
  { key: '25', path: 'News', routineKey: '25' },
  { key: '17', path: 'Sports', routineKey: '17' },
   { key: '22', path: 'Finance', routineKey: '22' },
  { key: '30', path: 'NBA', routineKey: '30' },
  { key: '31', path: 'NHL', routineKey: '31' },
  { key: '32', path: 'NFL', routineKey: '32' },
  { key: '33', path: 'Soccer', routineKey: '33' },
  { key: '35', path: 'Trump', routineKey: '35' },
  { key: '36', path: 'Ukraine', routineKey: '36' },
  { key: '37', path: 'Israel', routineKey: '37' },
  { key: '38', path: 'Messi', routineKey: '38' },
];
